import { categories, brandNames } from '@/mocks/categories';

export function QuickAnswerBox() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="bg-primary-50 border border-primary-200/60 rounded-2xl p-8 md:p-10">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
            What is <strong>CSSBuy Spreadsheet</strong>?
          </h2>
          <p className="text-base text-foreground-700 leading-relaxed">
            A <strong>CSSBuy Spreadsheet</strong> is a curated, community-maintained collection of product links organized in a spreadsheet format, specifically designed for CSSBuy users. It contains thousands of verified product listings from trusted Chinese sellers across categories like sneakers, clothing, accessories, and luxury items. Each entry typically includes direct purchase links, QC reference photos, seller ratings, and pricing information&mdash;making it the most efficient way to discover and purchase products through CSSBuy without hours of manual searching.
          </p>
        </div>
      </div>
    </section>
  );
}

export function WhyUseSection() {
  const reasons = [
    { icon: 'ri-timer-line', title: 'Save Time', desc: 'Skip the endless scrolling through forums and seller pages. Our CSSBuy Spreadsheet consolidates thousands of verified products into one organized, searchable format so you can find exactly what you want in seconds rather than hours.' },
    { icon: 'ri-verified-badge-line', title: 'Verified Products', desc: 'Every product in our spreadsheet has been vetted by community members and experienced buyers. We only include listings from trusted sellers with proven track records of quality products and reliable shipping.' },
    { icon: 'ri-camera-line', title: 'QC Photos', desc: 'Access real Quality Control photos for most listings before you buy. See actual product images taken at warehouses&mdash;not just polished marketing shots&mdash;so you know exactly what you are getting.' },
    { icon: 'ri-user-star-line', title: 'Trusted Sellers', desc: 'We maintain a curated list of the most reliable sellers in the CSSBuy ecosystem. Each seller is rated based on community feedback, order fulfillment rates, and product quality consistency.' },
    { icon: 'ri-money-dollar-circle-line', title: 'Lower Prices', desc: 'By aggregating listings from multiple sellers, our spreadsheet lets you compare prices at a glance. Find the best deals without compromising on quality, often saving 20-40 percent compared to retail.' },
    { icon: 'ri-compass-line', title: 'Easy Navigation', desc: 'Our spreadsheet is organized by categories, brands, and popularity. Whether you are hunting for the latest Jordan release or a specific luxury accessory, intuitive filtering gets you there fast.' },
    { icon: 'ri-loop-left-line', title: 'Regular Updates', desc: 'New products are added daily and outdated listings are removed promptly. Our community contributors ensure the spreadsheet stays current with the latest drops and trending items.' },
    { icon: 'ri-emotion-happy-line', title: 'Beginner Friendly', desc: 'New to CSSBuy? Our spreadsheet includes a comprehensive getting-started section with step-by-step guides, shipping tips, and everything a first-time buyer needs to shop with confidence.' },
  ];

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-wider bg-primary-50 px-3 py-1 rounded-full mb-4">Why Choose Us</span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
            Why Use <strong>CSSBuy Spreadsheet</strong>?
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Thousands of smart shoppers rely on our spreadsheet every day. Here is why it is the ultimate tool for CSSBuy shopping.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {reasons.map((item, i) => (
            <div key={i} className="bg-background-50 rounded-xl p-6 hover:bg-background-50 transition-colors group">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                <i className={`${item.icon} text-lg text-primary-600`}></i>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StatsSection() {
  const stats = [
    { value: '10,000+', label: 'Products Indexed' },
    { value: '14', label: 'Categories' },
    { value: '200+', label: 'Brands' },
    { value: 'Daily', label: 'Updates' },
    { value: '100%', label: 'Free Access' },
  ];

  return (
    <section className="py-14 md:py-18 bg-primary-600">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 md:gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-1">{stat.value}</div>
              <div className="text-xs md:text-sm text-primary-200 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CategoriesGrid() {
  return (
    <section id="categories" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-wider bg-accent-50 px-3 py-1 rounded-full mb-4">Browse</span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
            Explore <strong>CSSBuy Spreadsheet</strong> Categories
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Find exactly what you are looking for across our comprehensive category collection. Each category contains hundreds of verified product listings.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop="true">
          {categories.map((cat, i) => (
            <a
              key={i}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 rounded-xl p-5 text-center transition-all cursor-pointer"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-50 group-hover:bg-primary-100 flex items-center justify-center transition-colors">
                <i className="ri-shopping-bag-3-line text-lg text-primary-600"></i>
              </div>
              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 whitespace-nowrap transition-colors">{cat.name}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PopularBrands() {
  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold text-secondary-600 uppercase tracking-wider bg-secondary-50 px-3 py-1 rounded-full mb-4">Top Brands</span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
            Popular Brands in <strong>CSSBuy Spreadsheet</strong>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            From streetwear giants to luxury houses, our spreadsheet covers the most sought-after brands worldwide.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {brandNames.map((brand, i) => (
            <div key={i} className="bg-background-50 rounded-xl p-5 text-center hover:bg-background-100 transition-colors">
              <span className="font-heading text-base font-semibold text-foreground-800">{brand}</span>
              <p className="text-xs text-foreground-500 mt-1">Verified listings available</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { num: '01', icon: 'ri-search-line', title: 'Find Product', desc: 'Browse through our CSSBuy Spreadsheet categories or use the search function to locate the exact item you want. Each listing is organized with clear labels, making it effortless to navigate through thousands of products across sneakers, clothing, accessories, and more. You can filter by brand, price range, or seller rating to narrow down your options quickly.' },
    { num: '02', icon: 'ri-file-list-3-line', title: 'Check Spreadsheet', desc: 'Open the product link and review all available details in the spreadsheet. Each entry includes the seller name, product code, size availability, color options, and current pricing. Take your time comparing similar items from different sellers to ensure you are getting the best possible deal for your budget.' },
    { num: '03', icon: 'ri-external-link-line', title: 'Open Product Link', desc: 'Click through to the seller page on the CSSBuy platform. The spreadsheet links take you directly to the product listing where you can view more detailed specifications, read seller descriptions, and check the most recent buyer reviews and ratings before making your purchase decision.' },
    { num: '04', icon: 'ri-camera-lens-line', title: 'View QC Photos', desc: 'Before finalizing your order, examine the Quality Control photos available in the spreadsheet. These real warehouse images show the actual product you will receive, not just catalog shots. Check for accurate color representation, logo placement, stitching quality, and overall craftsmanship to ensure satisfaction.' },
    { num: '05', icon: 'ri-shopping-cart-line', title: 'Submit Order', desc: 'Once you are confident in your selection, place your order through CSSBuy. The spreadsheet provides guidance on sizing recommendations and seller communication tips. Many listings also include discount codes or special pricing notes that can help you save even more on your purchase.' },
    { num: '06', icon: 'ri-flight-takeoff-line', title: 'International Shipping', desc: 'After your order is processed, choose from multiple international shipping options. Our spreadsheet includes a shipping guide section with estimated delivery times, carrier comparisons, and customs tips for different regions. Track your package every step of the way until it arrives at your door.' },
  ];

  return (
    <section id="how-it-works" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-wider bg-primary-50 px-3 py-1 rounded-full mb-4">Process</span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
            How <strong>CSSBuy Spreadsheet</strong> Works
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Getting started is simple. Follow these six steps to discover and purchase products through our spreadsheet.
          </p>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute left-[39px] top-12 bottom-12 w-px bg-primary-200"></div>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-5 items-start relative">
                <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-primary-500 text-background-50 flex flex-col items-center justify-center z-10">
                  <span className="text-xs font-bold opacity-80">{step.num}</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${step.icon} text-lg`}></i>
                  </div>
                </div>
                <div className="flex-1 bg-background-100 rounded-xl p-6 md:p-7">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}